/**
 * Dados simulados do modulo de leads (RNF19) -- NAO E PRODUCAO.
 *
 * Um POOL deterministico por empresa: os seis leads escritos a mao (ids
 * SIMULADO-lead-001..006, as situacoes que o motor produz de verdade) mais
 * ate ~720 leads gerados por formula, espalhados por ~12 meses, para a lista
 * exercitar busca, ordenacao, filtros combinados, facetas e "ver mais" em
 * volume parecido com o de uma empresa real. Sem Math.random: o mesmo id
 * gera sempre a mesma historia, em qualquer tela.
 *
 * Como a lista e o detalhe saem do MESMO pool, quem clica numa linha nunca
 * encontra outra historia no detalhe. A situacao, o ultimo contato e o
 * proximo toque da linha sao calculados por `resumo.ts` -- as mesmas funcoes
 * que a pagina de detalhe usa -- entao a tabela e o detalhe nunca discordam.
 *
 * O que muda por empresa: volume (fator), produtos (catalogo), comissao
 * (`feeBps`), nome da vendedora (equipe) e a presenca do funil de expirados
 * (so quem tem oferta recorrente e o funil ligado no cadastro).
 *
 * A janela de tempo e interpretada em TAMANHO (N dias antes de
 * AGORA_SIMULADO), nao em instantes absolutos: a previa pode ser aberta em
 * qualquer data e continua mostrando dado.
 *
 * Toda linha do tempo comeca no FATO (abandono ou vencimento). Nenhum toque
 * e gerado antes dele: a regua so age sobre quem ja saiu.
 */

import { AGORA_SIMULADO, gravacaoSimulada, menosMinutos } from '../../lib/api';
import { empresaPorId, equipeDaEmpresa, ofertaCiclica, ofertasDaEmpresa } from '../../lib/mock/catalogo';
import type { FunnelKind, PeriodRange, ProductOffer, Provider, RecoveryJobStatus, RecoveryTier } from '../../types';
import { aplicarFiltros, combinaComBusca, contarFacetas, ordenar } from './filtros';
import { contatosQueContam, proximoToque, situacaoDoLead, ultimoContato } from './resumo';
import type {
  ConsultaDeLeads,
  DegrauDaRegua,
  LeadConversion,
  LeadDetail,
  LeadListRow,
  LeadRuleState,
  PaginaDeLeads,
  ResumoDeLeads,
  TimelineEvent,
  VoiceCallStatus,
} from './types';
import { rotuloDoAtraso, SITUACAO } from './vocabulario';

const FEE_PADRAO_BPS = 1_500;
const TETO_LIGACOES = 2;
const FILA_MINUTOS = 30;
const HORA = 60;
const DIA = 1_440;
const TAMANHO_DO_POOL = 1_500;
/** Espacamento medio entre leads gerados: ~4 por dia na empresa demo, ~12 meses no ultimo indice. */
const ESPACAMENTO_MIN = 350;
const ERRO_EVOLUTION = 'Instância da Evolution API sem sessão ativa.';

/** Mesmos fatores de volume de lib/api.ts (a empresa demo e 100%). */
const FATOR_POR_EMPRESA: Readonly<Record<string, number>> = {
  'SIMULADO-empresa-demo': 100,
  'SIMULADO-empresa-fit': 62,
  'SIMULADO-empresa-dev': 38,
  'SIMULADO-empresa-bem': 12,
};

const REGUA_CARRINHO: readonly DegrauDaRegua[] = [
  { touchId: 'T30m', label: rotuloDoAtraso(30), channel: 'voice', order: 1, delayMinutes: 30, isLast: false },
  { touchId: 'T1h', label: rotuloDoAtraso(HORA), channel: 'whatsapp', order: 2, delayMinutes: HORA, isLast: false },
  { touchId: 'T24h', label: rotuloDoAtraso(24 * HORA), channel: 'whatsapp', order: 3, delayMinutes: 24 * HORA, isLast: false },
  { touchId: 'T48h', label: rotuloDoAtraso(48 * HORA), channel: 'whatsapp', order: 4, delayMinutes: 48 * HORA, isLast: true },
];

const REGUA_EXPIRADOS: readonly DegrauDaRegua[] = [
  { touchId: 'D15', label: 'D+15', channel: 'whatsapp', order: 1, delayMinutes: 15 * DIA, isLast: false },
  { touchId: 'D30', label: 'D+30', channel: 'whatsapp', order: 2, delayMinutes: 30 * DIA, isLast: false },
  { touchId: 'D60', label: 'D+60', channel: 'whatsapp', order: 3, delayMinutes: 60 * DIA, isLast: false },
  { touchId: 'D90', label: 'D+90', channel: 'whatsapp', order: 4, delayMinutes: 90 * DIA, isLast: true },
];

const NOMES: readonly string[] = [
  'Ana', 'Bruno', 'Carla', 'Daniel', 'Eduarda', 'Felipe', 'Gabriela', 'Henrique', 'Isabela', 'Júlio', 'Karina', 'Leonardo',
  'Marina', 'Nicolas', 'Olívia', 'Pedro', 'Rafaela', 'Samuel', 'Tatiane', 'Vinícius', 'Yasmin', 'William', 'Lívia', 'Otávio',
];
const SOBRENOMES: readonly string[] = [
  'Almeida', 'Barbosa', 'Cardoso', 'Duarte', 'Esteves', 'Freitas', 'Gonçalves', 'Barros', 'Ibrahim', 'Jardim',
  'Klein', 'Lacerda', 'Moraes', 'Nogueira', 'Oliveira', 'Pacheco', 'Queiroz', 'Ribeiro', 'Siqueira', 'Teixeira',
];
const DDDS: readonly string[] = ['11', '21', '31', '41', '51', '61', '71', '81', '85', '92', '19', '27', '47', '62'];
const RESPOSTAS: readonly string[] = [
  'Tenho uma dúvida sobre o pagamento, dá pra parcelar em mais vezes?',
  'O cartão não passou na hora. Vocês têm boleto ou PIX?',
  'Vi a mensagem, posso pagar amanhã quando cair o salário?',
  'Quero sim! Me manda o link de novo, por favor.',
  'Quanto fica à vista?',
];
const BOTAO_CARRINHO = 'Finalizar compra';
const BOTAO_EXPIRADOS = 'Reativar acesso';

// -----------------------------------------------------------------------------
// Utilitarios deterministicos
// -----------------------------------------------------------------------------

/** Hash FNV-1a de 32 bits: barato, estavel e sem dependencia. */
function hashString(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/** Prefixo do id simulado. A previa so gera kiwify/hotmart/shopify; os demais existem para o Record ficar completo. */
const PREFIXO_PEDIDO: Readonly<Record<Provider, string>> = {
  kiwify: 'ord',
  hotmart: 'HP',
  shopify: 'gid',
  generic: 'cf',
  hubla: 'hub',
  eduzz: 'edz',
  cakto: 'ck',
  greenn: 'grn',
};

function idExterno(provider: Provider, leadId: string): string {
  return `${PREFIXO_PEDIDO[provider]}_${hashString(leadId).toString(16).padStart(8, '0').slice(0, 8)}`;
}

/**
 * Comissao em inteiros: (valor x bps + 5000) / 10000, truncado = arredonda
 * meio para cima sem passar por float. Aqui o gerador faz o papel do servidor;
 * a tela nunca calcula comissao por conta propria.
 */
function comissaoCents(amountCents: number, feeBps: number): number {
  return Math.floor((amountCents * feeBps + 5_000) / 10_000);
}

function emailFicticio(name: string | null): string | null {
  if (name === null) {
    return null;
  }
  const local = name
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter((p) => p.length > 0);
  const primeiro = local[0] ?? 'lead';
  const ultimo = local.length > 1 ? local[local.length - 1] ?? '' : '';
  return ultimo === '' ? `${primeiro}@email.exemplo` : `${primeiro}.${ultimo}@email.exemplo`;
}

function primeiroNome(name: string | null): string {
  const partes = (name ?? '').trim().split(/\s+/).filter((p) => p.length > 0);
  return partes[0] ?? 'A pessoa';
}

function fatorDaEmpresa(companyId: string): number {
  return FATOR_POR_EMPRESA[companyId] ?? 50;
}

/** Percentil 80 dos valores (inteiro, em centavos). Mesma regra de `api.ts` para o backend de hoje. */
export function limiarAltoValor(valoresCents: readonly number[]): number {
  if (valoresCents.length === 0) {
    return 1;
  }
  const ordenados = [...valoresCents].sort((a, b) => a - b);
  const indice = Math.min(ordenados.length - 1, Math.floor(ordenados.length * 0.8));
  return Math.max(1, ordenados[indice] ?? 1);
}

// -----------------------------------------------------------------------------
// Base do pool
// -----------------------------------------------------------------------------

interface LeadBase {
  readonly index: number;
  readonly id: string;
  readonly name: string | null;
  readonly phoneE164: string;
  readonly funnel: FunnelKind;
  /** Posicao no catalogo da empresa; `null` = o provedor mandou sem produto. */
  readonly ofertaIndex: number | null;
  readonly providerSemOferta: Provider;
  readonly amountSemOfertaCents: number;
  /** Minutos antes de AGORA_SIMULADO em que o fato aconteceu. */
  readonly abandonedMinAgo: number;
  readonly especial: boolean;
}

/** Os seis escritos a mao. Mesma ordem e mesmos telefones da lista antiga de lib/api.ts. */
const LEADS_ESPECIAIS: readonly LeadBase[] = [
  { index: 1, id: 'SIMULADO-lead-001', name: 'Mariana Ferraz', phoneE164: '+5511999880001', funnel: 'carrinho_abandonado', ofertaIndex: 0, providerSemOferta: 'kiwify', amountSemOfertaCents: 49_700, abandonedMinAgo: 12, especial: true },
  { index: 2, id: 'SIMULADO-lead-002', name: 'João Batista Lemos', phoneE164: '+5592988770002', funnel: 'carrinho_abandonado', ofertaIndex: 1, providerSemOferta: 'hotmart', amountSemOfertaCents: 129_900, abandonedMinAgo: 46, especial: true },
  { index: 3, id: 'SIMULADO-lead-003', name: 'Aline Souza Prado', phoneE164: '+5521977660003', funnel: 'carrinho_abandonado', ofertaIndex: 5, providerSemOferta: 'kiwify', amountSemOfertaCents: 89_700, abandonedMinAgo: 180, especial: true },
  { index: 4, id: 'SIMULADO-lead-004', name: 'Ricardo Nunes', phoneE164: '+5531966550004', funnel: 'carrinho_abandonado', ofertaIndex: 3, providerSemOferta: 'kiwify', amountSemOfertaCents: 39_700, abandonedMinAgo: 240, especial: true },
  { index: 5, id: 'SIMULADO-lead-005', name: 'Beatriz Camargo', phoneE164: '+5585955440005', funnel: 'carrinho_abandonado', ofertaIndex: 0, providerSemOferta: 'kiwify', amountSemOfertaCents: 49_700, abandonedMinAgo: 300, especial: true },
  { index: 6, id: 'SIMULADO-lead-006', name: null, phoneE164: '+5541944330006', funnel: 'carrinho_abandonado', ofertaIndex: null, providerSemOferta: 'hotmart', amountSemOfertaCents: 89_900, abandonedMinAgo: 8, especial: true },
];

function empresaTemExpirados(companyId: string): boolean {
  const empresa = empresaPorId(companyId);
  return empresa !== null && empresa.funnels.includes('expirados') && ofertasDaEmpresa(companyId).some((o) => o.recurring);
}

/** Lead gerado `i` da empresa, ou `null` quando o fator de volume o exclui. */
function gerarBase(companyId: string, i: number): LeadBase | null {
  const h = hashString(`${companyId}:lead:${i}`);
  if (h % 100 >= fatorDaEmpresa(companyId)) {
    return null;
  }
  const funnel: FunnelKind = empresaTemExpirados(companyId) && (h >>> 8) % 10 < 3 ? 'expirados' : 'carrinho_abandonado';
  const semNome = (h >>> 3) % 11 === 0;
  const semProduto = funnel === 'carrinho_abandonado' && (h >>> 6) % 23 === 0;
  const nome = NOMES[(h >>> 4) % NOMES.length] ?? 'Lead';
  const sobrenome = SOBRENOMES[(h >>> 9) % SOBRENOMES.length] ?? 'Silva';
  const segundo = (h >>> 13) % 3 === 0 ? ` ${SOBRENOMES[(h >>> 17) % SOBRENOMES.length] ?? ''}` : '';
  const ddd = DDDS[(h >>> 2) % DDDS.length] ?? '11';
  const telefone = `+55${ddd}9${String(1_000 + ((h >>> 5) % 9_000))}${String(1_000 + ((h >>> 11) % 9_000))}`;
  // Linear com jitter: densidade parecida em qualquer janela, para a comparacao com o
  // periodo anterior variar so pelo sorteio de inclusao (como uma empresa real varia).
  const abandonedMinAgo = 35 + i * ESPACAMENTO_MIN + ((h >>> 14) % 7) * 13;
  return {
    index: i,
    id: `SIMULADO-lead-${String(i).padStart(3, '0')}`,
    name: semNome ? null : `${nome} ${sobrenome}${segundo}`.trim(),
    phoneE164: telefone,
    funnel,
    ofertaIndex: semProduto ? null : ((h >>> 7) % 13) + i,
    providerSemOferta: (h >>> 12) % 2 === 0 ? 'kiwify' : 'hotmart',
    amountSemOfertaCents: 29_900 + ((h >>> 10) % 7) * 10_000,
    abandonedMinAgo,
    especial: false,
  };
}

interface OfertaResolvida {
  readonly offerId: string | null;
  readonly productName: string | null;
  readonly amountCents: number;
  readonly provider: Provider;
}

function resolverOferta(companyId: string, base: LeadBase): OfertaResolvida {
  if (base.ofertaIndex === null) {
    return { offerId: null, productName: null, amountCents: base.amountSemOfertaCents, provider: base.providerSemOferta };
  }
  let oferta: ProductOffer;
  if (base.funnel === 'expirados') {
    const recorrentes = ofertasDaEmpresa(companyId).filter((o) => o.recurring);
    oferta = recorrentes[base.ofertaIndex % Math.max(1, recorrentes.length)] ?? ofertaCiclica(companyId, base.ofertaIndex);
  } else {
    oferta = ofertaCiclica(companyId, base.ofertaIndex);
  }
  return { offerId: oferta.id, productName: oferta.name, amountCents: oferta.priceCents, provider: oferta.provider };
}

// -----------------------------------------------------------------------------
// Montagem da linha do tempo
// -----------------------------------------------------------------------------

interface Contexto {
  readonly base: LeadBase;
  readonly oferta: OfertaResolvida;
  readonly feeBps: number;
  readonly vendedora: string;
  readonly externalId: string;
}

interface Montagem {
  readonly timeline: readonly TimelineEvent[];
  readonly jobStatus: RecoveryJobStatus;
  readonly tier: RecoveryTier;
  readonly voiceAttempts: number;
  readonly recoveredAt: string | null;
  readonly conversion: LeadConversion | null;
  readonly nextTouch: LeadRuleState['nextTouch'];
}

/** `Omit` distributivo: tira o `id` de CADA membro da uniao, nao da intersecao das chaves. */
type SemId<T> = T extends unknown ? Omit<T, 'id'> : never;

/** Acumulador de eventos com ids sequenciais e relogio relativo a AGORA_SIMULADO. */
class Linha {
  private n = 0;
  public readonly eventos: TimelineEvent[] = [];

  public constructor(private readonly leadId: string) {}

  public add(evento: SemId<TimelineEvent>): void {
    this.n += 1;
    const completo = { ...evento, id: `${this.leadId}-ev-${String(this.n).padStart(2, '0')}` } as TimelineEvent;
    this.eventos.push(completo);
  }
}

/** `menosMinutos` com piso em 1 minuto: nada gerado fica "no futuro" por arredondamento. */
function tp(minAgo: number): string {
  return menosMinutos(Math.max(1, minAgo));
}

function seed(leadId: string, n: number): number {
  return (hashString(`${leadId}:${n}`) % 1_000) / 1_000;
}

function resumoDaLigacao(status: VoiceCallStatus, nome: string, pagaNaVoz: boolean): string | null {
  switch (status) {
    case 'answered':
      return pagaNaVoz
        ? `${nome} atendeu, tirou uma dúvida sobre o parcelamento e pagou ainda durante a ligação; o provedor confirmou minutos depois.`
        : `${nome} atendeu, confirmou o interesse e pediu o link de pagamento pelo WhatsApp.`;
    case 'voicemail':
      return 'Caiu na caixa postal. A IA deixou o recado e encerrou; a caixa postal fecha o laço de voz na hora (RF08).';
    case 'no_answer':
      return 'Chamou até o limite e ninguém atendeu.';
    case 'failed':
      return 'A chamada não completou (operadora).';
    default: {
      const nunca: never = status;
      return nunca;
    }
  }
}

function conversaoDe(pagoEm: number, amountCents: number, feeBps: number, canal: 'voice' | 'whatsapp', touchLabel: string, contatoEm: number): LeadConversion {
  return {
    paidAt: tp(pagoEm),
    amountCents,
    commissionCents: comissaoCents(amountCents, feeBps),
    feeBpsApplied: feeBps,
    creditedTo: { channel: canal, touchLabel, at: tp(contatoEm) },
    reversed: false,
  };
}

type Modelo = 'recuperado' | 'pagou_sozinho' | 'falhou' | 'com_comercial' | 'cancelado' | 'andando';

function montarCarrinho(ctx: Contexto): Montagem {
  const { base, oferta, feeBps, vendedora, externalId } = ctx;
  const A = base.abandonedMinAgo;
  const r = (n: number): number => seed(base.id, n);
  const linha = new Linha(base.id);
  const nome = primeiroNome(base.name);
  const valor = oferta.amountCents;

  linha.add({ kind: 'webhook_received', at: tp(A), provider: oferta.provider, eventLabel: 'carrinho abandonado', amountCents: valor, externalId });
  linha.add({ kind: 'queued', at: tp(A), delayMinutes: FILA_MINUTOS, runAt: menosMinutos(A - FILA_MINUTOS) });

  if (A < FILA_MINUTOS + 1) {
    return {
      timeline: linha.eventos,
      jobStatus: 'pending',
      tier: 'tier1_voice',
      voiceAttempts: 0,
      recoveredAt: null,
      conversion: null,
      nextTouch: { touch: { touchId: 'T30m', label: rotuloDoAtraso(FILA_MINUTOS), channel: 'voice' }, scheduledAt: menosMinutos(A - FILA_MINUTOS) },
    };
  }

  const p = r(0);
  let modelo: Modelo = p < 0.24 ? 'recuperado' : p < 0.31 ? 'pagou_sozinho' : p < 0.335 ? 'falhou' : p < 0.415 ? 'com_comercial' : p < 0.43 ? 'cancelado' : 'andando';
  if (modelo === 'com_comercial' && A < 95) {
    modelo = 'andando';
  }

  if (modelo === 'pagou_sozinho') {
    const pagoEm = A - Math.min(A - 1, 4 + Math.floor(r(1) * 24));
    linha.add({ kind: 'payment_approved', at: tp(pagoEm), provider: oferta.provider, amountCents: valor, creditedTo: null, commissionCents: 0, feeBpsApplied: feeBps });
    return { timeline: linha.eventos, jobStatus: 'aborted', tier: 'tier1_voice', voiceAttempts: 0, recoveredAt: tp(pagoEm), conversion: null, nextTouch: null };
  }

  // --- Tier 1: voz (ate 2 ligacoes; caixa postal fecha o laco) -------------
  const caminho: 'voz' | 'whatsapp' | null = modelo === 'recuperado' ? (r(2) < 0.45 || A < 62 ? 'voz' : 'whatsapp') : null;
  const inicio1 = A - FILA_MINUTOS;
  let voiceAttempts = 1;
  const c1: VoiceCallStatus = caminho === 'voz' && r(3) < 0.7 ? 'answered' : r(3) < 0.3 ? 'voicemail' : 'no_answer';
  linha.add({
    kind: 'voice_call',
    at: tp(inicio1),
    attempt: 1,
    maxAttempts: TETO_LIGACOES,
    status: c1,
    durationSeconds: c1 === 'answered' ? 60 + Math.floor(r(4) * 120) : c1 === 'voicemail' ? 9 + Math.floor(r(4) * 10) : null,
    recordingUrl: c1 === 'answered' ? gravacaoSimulada(6) : c1 === 'voicemail' ? gravacaoSimulada(9) : null,
    summary: resumoDaLigacao(c1, nome, caminho === 'voz'),
    touchLabel: rotuloDoAtraso(FILA_MINUTOS),
  });

  if (c1 === 'answered') {
    const pagoEm = Math.max(1, inicio1 - (3 + Math.floor(r(5) * 25)));
    const conversao = conversaoDe(pagoEm, valor, feeBps, 'voice', rotuloDoAtraso(FILA_MINUTOS), inicio1);
    linha.add({ kind: 'payment_approved', at: conversao.paidAt, provider: oferta.provider, amountCents: valor, creditedTo: conversao.creditedTo, commissionCents: conversao.commissionCents, feeBpsApplied: feeBps });
    return { timeline: linha.eventos, jobStatus: 'succeeded', tier: 'tier1_voice', voiceAttempts, recoveredAt: conversao.paidAt, conversion: conversao, nextTouch: null };
  }

  if (c1 === 'no_answer' && inicio1 - 3 >= 1) {
    voiceAttempts = 2;
    const c2: VoiceCallStatus = caminho === 'voz' ? 'answered' : r(6) < 0.35 ? 'voicemail' : 'no_answer';
    const inicio2 = inicio1 - 3;
    linha.add({
      kind: 'voice_call',
      at: tp(inicio2),
      attempt: 2,
      maxAttempts: TETO_LIGACOES,
      status: c2,
      durationSeconds: c2 === 'answered' ? 50 + Math.floor(r(7) * 110) : c2 === 'voicemail' ? 8 + Math.floor(r(7) * 9) : null,
      recordingUrl: c2 === 'answered' ? gravacaoSimulada(6) : c2 === 'voicemail' ? gravacaoSimulada(9) : null,
      summary: c2 === 'answered' ? resumoDaLigacao(c2, nome, true) : c2 === 'voicemail' ? 'Caixa postal na 2ª tentativa; teto de ligações atingido (RF08), o motor avançou para o WhatsApp.' : 'Segunda tentativa sem resposta; teto de ligações atingido (RF08).',
      touchLabel: rotuloDoAtraso(FILA_MINUTOS),
    });
    if (c2 === 'answered') {
      const pagoEm = Math.max(1, inicio2 - (3 + Math.floor(r(5) * 25)));
      const conversao = conversaoDe(pagoEm, valor, feeBps, 'voice', rotuloDoAtraso(FILA_MINUTOS), inicio2);
      linha.add({ kind: 'payment_approved', at: conversao.paidAt, provider: oferta.provider, amountCents: valor, creditedTo: conversao.creditedTo, commissionCents: conversao.commissionCents, feeBpsApplied: feeBps });
      return { timeline: linha.eventos, jobStatus: 'succeeded', tier: 'tier1_voice', voiceAttempts, recoveredAt: conversao.paidAt, conversion: conversao, nextTouch: null };
    }
  }

  // --- Tier 2: WhatsApp T+1h, T+24h, T+48h ----------------------------------
  const passos = REGUA_CARRINHO.filter((d) => d.channel === 'whatsapp');
  const alcancados = passos.filter((s) => A >= s.delayMinutes + 4);
  const escolhido = (caminho === 'whatsapp' || modelo === 'com_comercial') && alcancados.length > 0 ? alcancados[Math.floor(r(8) * alcancados.length)] : undefined;
  let tier: RecoveryTier = 'tier1_voice';
  let nextTouch: LeadRuleState['nextTouch'] = null;

  for (let k = 0; k < passos.length; k += 1) {
    const s = passos[k];
    if (s === undefined) {
      break;
    }
    if (A < s.delayMinutes + 1) {
      nextTouch = { touch: { touchId: s.touchId, label: s.label, channel: 'whatsapp' }, scheduledAt: menosMinutos(A - s.delayMinutes) };
      break;
    }
    tier = 'tier2_whatsapp';
    const envio = A - s.delayMinutes;

    if (modelo === 'falhou' && k === 0) {
      const assunto = `${nome}, seu ${oferta.productName ?? 'pedido'} ficou parado no checkout`;
      linha.add({ kind: 'whatsapp', at: tp(envio), touchLabel: s.label, stage: 'failed', buttonLabel: null, errorMessage: ERRO_EVOLUTION });
      linha.add({ kind: 'email', at: tp(envio), touchLabel: s.label, stage: 'sent', subject: assunto, fallback: true });
      linha.add({ kind: 'job_failed', at: tp(envio - 1), lastError: `Tier 2 falhou: ${ERRO_EVOLUTION} Régua encerrada e lead liberado.` });
      if (envio - 16 >= 1 && r(9) < 0.5) {
        linha.add({ kind: 'email', at: tp(envio - 15), touchLabel: s.label, stage: 'opened', subject: assunto, fallback: true });
      }
      return { timeline: linha.eventos, jobStatus: 'failed', tier, voiceAttempts, recoveredAt: null, conversion: null, nextTouch: null };
    }

    linha.add({ kind: 'whatsapp', at: tp(envio), touchLabel: s.label, stage: 'sent', buttonLabel: BOTAO_CARRINHO, errorMessage: null });
    linha.add({ kind: 'whatsapp', at: tp(envio - 0.3), touchLabel: s.label, stage: 'delivered', buttonLabel: null, errorMessage: null });
    const leu = r(10 + k) < 0.62 && envio - 3 >= 1;
    const leituraEm = envio - Math.min(envio - 1, 2 + Math.floor(r(20 + k) * 200));
    if (leu) {
      linha.add({ kind: 'whatsapp', at: tp(leituraEm), touchLabel: s.label, stage: 'read', buttonLabel: null, errorMessage: null });
    }

    if (escolhido !== undefined && escolhido.touchId === s.touchId) {
      const baseLeitura = leu ? leituraEm : Math.max(2, envio - 1);
      if (!leu) {
        linha.add({ kind: 'whatsapp', at: tp(baseLeitura), touchLabel: s.label, stage: 'read', buttonLabel: null, errorMessage: null });
      }
      if (caminho === 'whatsapp') {
        const clique = Math.max(2, baseLeitura - 1);
        const pagoEm = Math.max(1, clique - (1 + Math.floor(r(11) * 6)));
        linha.add({ kind: 'whatsapp', at: tp(clique), touchLabel: s.label, stage: 'clicked', buttonLabel: BOTAO_CARRINHO, errorMessage: null });
        const conversao = conversaoDe(pagoEm, valor, feeBps, 'whatsapp', s.label, envio);
        linha.add({ kind: 'payment_approved', at: conversao.paidAt, provider: oferta.provider, amountCents: valor, creditedTo: conversao.creditedTo, commissionCents: conversao.commissionCents, feeBpsApplied: feeBps });
        return { timeline: linha.eventos, jobStatus: 'succeeded', tier, voiceAttempts, recoveredAt: conversao.paidAt, conversion: conversao, nextTouch: null };
      }
      const respostaEm = Math.max(2, baseLeitura - 2);
      linha.add({ kind: 'lead_reply', at: tp(respostaEm), channel: 'whatsapp', text: RESPOSTAS[Math.floor(r(12) * RESPOSTAS.length)] ?? RESPOSTAS[0] ?? '' });
      linha.add({ kind: 'seller_took_over', at: tp(respostaEm - 1), sellerName: vendedora });
      return { timeline: linha.eventos, jobStatus: 'aborted', tier, voiceAttempts, recoveredAt: null, conversion: null, nextTouch: null };
    }

    if (modelo === 'cancelado' && k === 0 && A >= s.delayMinutes + 40) {
      linha.add({ kind: 'rule_removed', at: tp(envio - 30), reason: 'Removido pelo painel: a pessoa pediu para não receber mais mensagens.' });
      return { timeline: linha.eventos, jobStatus: 'aborted', tier, voiceAttempts, recoveredAt: null, conversion: null, nextTouch: null };
    }
  }

  const ultimo = passos[passos.length - 1];
  if (ultimo !== undefined && A >= ultimo.delayMinutes + HORA + 1) {
    linha.add({ kind: 'rule_ended', at: tp(A - ultimo.delayMinutes - HORA), reason: 'sem_resposta', touches: voiceAttempts + passos.length });
    return { timeline: linha.eventos, jobStatus: 'failed', tier: 'exhausted', voiceAttempts, recoveredAt: null, conversion: null, nextTouch: null };
  }
  return { timeline: linha.eventos, jobStatus: 'running', tier, voiceAttempts, recoveredAt: null, conversion: null, nextTouch };
}

function montarExpirados(ctx: Contexto): Montagem {
  const { base, oferta, feeBps, vendedora, externalId } = ctx;
  const A = base.abandonedMinAgo;
  const r = (n: number): number => seed(base.id, n);
  const linha = new Linha(base.id);
  const valor = oferta.amountCents;
  const primeiro = REGUA_EXPIRADOS[0];
  const espera = primeiro?.delayMinutes ?? 15 * DIA;

  linha.add({ kind: 'webhook_received', at: tp(A), provider: oferta.provider, eventLabel: 'assinatura vencida', amountCents: valor, externalId });
  linha.add({ kind: 'queued', at: tp(A), delayMinutes: espera, runAt: menosMinutos(A - espera) });

  if (A < espera + 1) {
    return {
      timeline: linha.eventos,
      jobStatus: 'pending',
      tier: 'tier2_whatsapp',
      voiceAttempts: 0,
      recoveredAt: null,
      conversion: null,
      nextTouch: primeiro === undefined ? null : { touch: { touchId: primeiro.touchId, label: primeiro.label, channel: 'whatsapp' }, scheduledAt: menosMinutos(A - espera) },
    };
  }

  const p = r(0);
  let modelo: Modelo = p < 0.2 ? 'recuperado' : p < 0.27 ? 'pagou_sozinho' : p < 0.33 ? 'com_comercial' : p < 0.35 ? 'cancelado' : 'andando';

  if (modelo === 'pagou_sozinho') {
    const pagoEm = A - Math.min(A - 1, 2 * DIA + Math.floor(r(1) * 12 * DIA));
    linha.add({ kind: 'payment_approved', at: tp(pagoEm), provider: oferta.provider, amountCents: valor, creditedTo: null, commissionCents: 0, feeBpsApplied: feeBps });
    return { timeline: linha.eventos, jobStatus: 'aborted', tier: 'tier2_whatsapp', voiceAttempts: 0, recoveredAt: tp(pagoEm), conversion: null, nextTouch: null };
  }

  const alcancados = REGUA_EXPIRADOS.filter((s) => A >= s.delayMinutes + HORA);
  if ((modelo === 'recuperado' || modelo === 'com_comercial') && alcancados.length === 0) {
    modelo = 'andando';
  }
  const escolhido = modelo === 'recuperado' || modelo === 'com_comercial' ? alcancados[Math.floor(r(8) * alcancados.length)] : undefined;
  let nextTouch: LeadRuleState['nextTouch'] = null;

  for (let k = 0; k < REGUA_EXPIRADOS.length; k += 1) {
    const s = REGUA_EXPIRADOS[k];
    if (s === undefined) {
      break;
    }
    if (A < s.delayMinutes + 1) {
      nextTouch = { touch: { touchId: s.touchId, label: s.label, channel: 'whatsapp' }, scheduledAt: menosMinutos(A - s.delayMinutes) };
      break;
    }
    const envio = A - s.delayMinutes;
    linha.add({ kind: 'whatsapp', at: tp(envio), touchLabel: s.label, stage: 'sent', buttonLabel: BOTAO_EXPIRADOS, errorMessage: null });
    linha.add({ kind: 'whatsapp', at: tp(envio - 0.4), touchLabel: s.label, stage: 'delivered', buttonLabel: null, errorMessage: null });
    const leu = r(10 + k) < 0.55 && envio - 31 >= 1;
    const leituraEm = envio - Math.min(envio - 1, 30 + Math.floor(r(20 + k) * 2 * DIA));
    if (leu) {
      linha.add({ kind: 'whatsapp', at: tp(leituraEm), touchLabel: s.label, stage: 'read', buttonLabel: null, errorMessage: null });
    }
    if (escolhido !== undefined && escolhido.touchId === s.touchId) {
      const baseLeitura = leu ? leituraEm : Math.max(2, envio - 1);
      if (!leu) {
        linha.add({ kind: 'whatsapp', at: tp(baseLeitura), touchLabel: s.label, stage: 'read', buttonLabel: null, errorMessage: null });
      }
      if (modelo === 'recuperado') {
        const clique = Math.max(2, baseLeitura - 1);
        const pagoEm = Math.max(1, clique - (2 + Math.floor(r(11) * 40)));
        linha.add({ kind: 'whatsapp', at: tp(clique), touchLabel: s.label, stage: 'clicked', buttonLabel: BOTAO_EXPIRADOS, errorMessage: null });
        const conversao = conversaoDe(pagoEm, valor, feeBps, 'whatsapp', s.label, envio);
        linha.add({ kind: 'payment_approved', at: conversao.paidAt, provider: oferta.provider, amountCents: valor, creditedTo: conversao.creditedTo, commissionCents: conversao.commissionCents, feeBpsApplied: feeBps });
        return { timeline: linha.eventos, jobStatus: 'succeeded', tier: 'tier2_whatsapp', voiceAttempts: 0, recoveredAt: conversao.paidAt, conversion: conversao, nextTouch: null };
      }
      const respostaEm = Math.max(2, baseLeitura - 3);
      linha.add({ kind: 'lead_reply', at: tp(respostaEm), channel: 'whatsapp', text: 'Parei de estudar por um tempo, mas quero voltar. Tem alguma condição pra retomar?' });
      linha.add({ kind: 'seller_took_over', at: tp(respostaEm - 1), sellerName: vendedora });
      return { timeline: linha.eventos, jobStatus: 'aborted', tier: 'tier2_whatsapp', voiceAttempts: 0, recoveredAt: null, conversion: null, nextTouch: null };
    }
    if (modelo === 'cancelado' && k === 0 && envio - 61 >= 1) {
      linha.add({ kind: 'rule_ended', at: tp(envio - 60), reason: 'descadastrou', touches: 1 });
      return { timeline: linha.eventos, jobStatus: 'failed', tier: 'exhausted', voiceAttempts: 0, recoveredAt: null, conversion: null, nextTouch: null };
    }
  }

  const ultimo = REGUA_EXPIRADOS[REGUA_EXPIRADOS.length - 1];
  if (ultimo !== undefined && A >= ultimo.delayMinutes + DIA + 1) {
    linha.add({ kind: 'rule_ended', at: tp(A - ultimo.delayMinutes - DIA), reason: 'sem_resposta', touches: REGUA_EXPIRADOS.length });
    return { timeline: linha.eventos, jobStatus: 'failed', tier: 'exhausted', voiceAttempts: 0, recoveredAt: null, conversion: null, nextTouch: null };
  }
  return { timeline: linha.eventos, jobStatus: 'running', tier: 'tier2_whatsapp', voiceAttempts: 0, recoveredAt: null, conversion: null, nextTouch };
}

/**
 * Os seis leads escritos a mao. Cada um conta uma situacao que o motor
 * produz de verdade:
 *  - 001 e 006: recem-chegados, esperando a fila de 30 minutos (RF04);
 *  - 002: caixa postal na 1a ligacao -> o laco de voz para na hora e a regua
 *    avanca para o WhatsApp T+1h (RF06/RF08);
 *  - 003: caminho feliz -- atendeu na 2a tentativa, pediu o link, clicou e
 *    pagou; credito ao ULTIMO contato (WhatsApp), comissao congelada (ADR-001);
 *  - 004: pagou sozinho 12 min depois, antes de qualquer contato -> regua
 *    cancelada sem comissao (RF05), e um reembolso depois sem nada a reverter;
 *  - 005: duas ligacoes sem sucesso, WhatsApp falhou (Evolution fora), e-mail
 *    de reserva aberto, lead respondeu e a vendedora assumiu; o job do motor
 *    ficou como `failed`.
 */
function montarEspecial(ctx: Contexto): Montagem {
  const { base, oferta, feeBps, vendedora, externalId } = ctx;
  const A = base.abandonedMinAgo;
  const linha = new Linha(base.id);
  const valor = oferta.amountCents;
  const filaRodaEm = A - FILA_MINUTOS;
  const t = (m: number): string => menosMinutos(m);

  linha.add({ kind: 'webhook_received', at: t(A), provider: oferta.provider, eventLabel: 'carrinho abandonado', amountCents: valor, externalId });
  linha.add({ kind: 'queued', at: t(A), delayMinutes: FILA_MINUTOS, runAt: t(filaRodaEm) });

  switch (base.id) {
    case 'SIMULADO-lead-002': {
      linha.add({
        kind: 'voice_call',
        at: t(filaRodaEm),
        attempt: 1,
        maxAttempts: TETO_LIGACOES,
        status: 'voicemail',
        durationSeconds: 14,
        recordingUrl: gravacaoSimulada(9),
        summary: 'Caiu na caixa postal. A IA deixou o recado e encerrou; a caixa postal fecha o laço de voz na hora (RF08).',
        touchLabel: rotuloDoAtraso(FILA_MINUTOS),
      });
      return {
        timeline: linha.eventos,
        jobStatus: 'running',
        tier: 'tier1_voice',
        voiceAttempts: 1,
        recoveredAt: null,
        conversion: null,
        nextTouch: { touch: { touchId: 'T1h', label: rotuloDoAtraso(HORA), channel: 'whatsapp' }, scheduledAt: t(A - HORA) },
      };
    }
    case 'SIMULADO-lead-003': {
      const pago = 131;
      const whatsappEm = pago + 1;
      const rotuloWhatsApp = rotuloDoAtraso(A - whatsappEm);
      const conversao: LeadConversion = {
        paidAt: t(pago),
        amountCents: valor,
        commissionCents: comissaoCents(valor, feeBps),
        feeBpsApplied: feeBps,
        creditedTo: { channel: 'whatsapp', touchLabel: rotuloWhatsApp, at: t(whatsappEm) },
        reversed: false,
      };
      linha.add({ kind: 'voice_call', at: t(filaRodaEm), attempt: 1, maxAttempts: TETO_LIGACOES, status: 'no_answer', durationSeconds: null, recordingUrl: null, summary: 'Chamou até o limite e ninguém atendeu.', touchLabel: rotuloDoAtraso(FILA_MINUTOS) });
      linha.add({
        kind: 'voice_call',
        at: t(pago + 2),
        attempt: 2,
        maxAttempts: TETO_LIGACOES,
        status: 'answered',
        durationSeconds: 96,
        recordingUrl: gravacaoSimulada(6),
        summary: `${primeiroNome(base.name)} atendeu, confirmou o interesse e pediu o link de pagamento pelo WhatsApp.`,
        touchLabel: rotuloDoAtraso(A - (pago + 2)),
      });
      linha.add({ kind: 'whatsapp', at: t(whatsappEm), touchLabel: rotuloWhatsApp, stage: 'sent', buttonLabel: BOTAO_CARRINHO, errorMessage: null });
      linha.add({ kind: 'whatsapp', at: t(whatsappEm - 0.2), touchLabel: rotuloWhatsApp, stage: 'delivered', buttonLabel: null, errorMessage: null });
      linha.add({ kind: 'whatsapp', at: t(whatsappEm - 0.4), touchLabel: rotuloWhatsApp, stage: 'read', buttonLabel: null, errorMessage: null });
      linha.add({ kind: 'lead_reply', at: t(whatsappEm - 0.5), channel: 'whatsapp', text: 'Recebi! Vou pagar agora mesmo, obrigada.' });
      linha.add({ kind: 'whatsapp', at: t(whatsappEm - 0.8), touchLabel: rotuloWhatsApp, stage: 'clicked', buttonLabel: BOTAO_CARRINHO, errorMessage: null });
      linha.add({ kind: 'payment_approved', at: t(pago), provider: oferta.provider, amountCents: valor, creditedTo: conversao.creditedTo, commissionCents: conversao.commissionCents, feeBpsApplied: feeBps });
      return { timeline: linha.eventos, jobStatus: 'succeeded', tier: 'tier2_whatsapp', voiceAttempts: 2, recoveredAt: t(pago), conversion: conversao, nextTouch: null };
    }
    case 'SIMULADO-lead-004': {
      const pago = 228;
      linha.add({ kind: 'payment_approved', at: t(pago), provider: oferta.provider, amountCents: valor, creditedTo: null, commissionCents: 0, feeBpsApplied: feeBps });
      linha.add({ kind: 'reversal', at: t(95), reason: 'refund', amountCents: valor, commissionRevertedCents: 0 });
      return { timeline: linha.eventos, jobStatus: 'aborted', tier: 'tier1_voice', voiceAttempts: 0, recoveredAt: t(pago), conversion: null, nextTouch: null };
    }
    case 'SIMULADO-lead-005': {
      const whatsappEm = A - HORA;
      const assunto = `${primeiroNome(base.name)}, seu ${oferta.productName ?? 'pedido'} ficou parado no checkout`;
      linha.add({ kind: 'voice_call', at: t(filaRodaEm), attempt: 1, maxAttempts: TETO_LIGACOES, status: 'no_answer', durationSeconds: null, recordingUrl: null, summary: null, touchLabel: rotuloDoAtraso(FILA_MINUTOS) });
      linha.add({
        kind: 'voice_call',
        at: t(filaRodaEm - 2),
        attempt: 2,
        maxAttempts: TETO_LIGACOES,
        status: 'voicemail',
        durationSeconds: 11,
        recordingUrl: gravacaoSimulada(9),
        summary: 'Caixa postal na 2ª tentativa; teto de ligações atingido (RF08), o motor avançou para o WhatsApp.',
        touchLabel: rotuloDoAtraso(FILA_MINUTOS),
      });
      linha.add({ kind: 'whatsapp', at: t(whatsappEm), touchLabel: rotuloDoAtraso(HORA), stage: 'failed', buttonLabel: null, errorMessage: ERRO_EVOLUTION });
      linha.add({ kind: 'email', at: t(whatsappEm), touchLabel: rotuloDoAtraso(HORA), stage: 'sent', subject: assunto, fallback: true });
      linha.add({ kind: 'job_failed', at: t(whatsappEm - 1), lastError: `Tier 2 falhou: ${ERRO_EVOLUTION} Régua encerrada e lead liberado.` });
      linha.add({ kind: 'email', at: t(whatsappEm - 14), touchLabel: rotuloDoAtraso(HORA), stage: 'opened', subject: assunto, fallback: true });
      linha.add({ kind: 'lead_reply', at: t(whatsappEm - 20), channel: 'email', text: 'O cartão não passou na hora. Vocês têm boleto ou PIX?' });
      linha.add({ kind: 'seller_took_over', at: t(whatsappEm - 30), sellerName: vendedora });
      return { timeline: linha.eventos, jobStatus: 'failed', tier: 'exhausted', voiceAttempts: 2, recoveredAt: null, conversion: null, nextTouch: null };
    }
    default:
      // 001 e 006: na fila de 30 minutos.
      return {
        timeline: linha.eventos,
        jobStatus: 'pending',
        tier: 'tier1_voice',
        voiceAttempts: 0,
        recoveredAt: null,
        conversion: null,
        nextTouch: { touch: { touchId: 'T30m', label: rotuloDoAtraso(FILA_MINUTOS), channel: 'voice' }, scheduledAt: t(filaRodaEm) },
      };
  }
}

// -----------------------------------------------------------------------------
// Pool por empresa (memoizado)
// -----------------------------------------------------------------------------

interface PoolDaEmpresa {
  readonly detalhes: readonly LeadDetail[];
  readonly porId: ReadonlyMap<string, LeadDetail>;
  readonly linhas: readonly LeadListRow[];
  readonly limiarAltoValorCents: number;
}

const CACHE_POOL = new Map<string, PoolDaEmpresa>();

function montarLead(companyId: string, base: LeadBase, limiarAltoValorCents: number | null): LeadDetail {
  const oferta = resolverOferta(companyId, base);
  const feeBps = empresaPorId(companyId)?.feeBps ?? FEE_PADRAO_BPS;
  const vendedora = equipeDaEmpresa(companyId).find((m) => m.role === 'member')?.name ?? 'Equipe comercial';
  const externalId = idExterno(oferta.provider, base.id);
  const ctx: Contexto = { base, oferta, feeBps, vendedora, externalId };
  const m = base.especial ? montarEspecial(ctx) : base.funnel === 'expirados' ? montarExpirados(ctx) : montarCarrinho(ctx);
  return {
    id: base.id,
    companyId,
    name: base.name,
    phoneE164: base.phoneE164,
    email: emailFicticio(base.name),
    provider: oferta.provider,
    externalId,
    offerId: oferta.offerId,
    productName: oferta.productName,
    amountCents: oferta.amountCents,
    funnel: base.funnel,
    jobStatus: m.jobStatus,
    tier: m.tier,
    abandonedAt: menosMinutos(base.abandonedMinAgo),
    recoveredAt: m.recoveredAt,
    highValue: limiarAltoValorCents !== null && oferta.amountCents >= limiarAltoValorCents,
    rule: {
      paused: false,
      nextTouch: m.nextTouch,
      voiceAttemptsUsed: m.voiceAttempts,
      voiceAttemptsMax: TETO_LIGACOES,
      ladder: base.funnel === 'expirados' ? REGUA_EXPIRADOS : REGUA_CARRINHO,
    },
    conversion: m.conversion,
    // O contrato promete ordem cronologica; o gerador empurra por historia, nao por hora.
    timeline: [...m.timeline].sort((a, b) => a.at.localeCompare(b.at)),
  };
}

function linhaDoLead(d: LeadDetail): LeadListRow {
  return {
    id: d.id,
    companyId: d.companyId,
    name: d.name,
    phoneE164: d.phoneE164,
    productName: d.productName,
    provider: d.provider,
    amountCents: d.amountCents,
    jobStatus: d.jobStatus,
    tier: d.tier,
    voiceAttempts: d.rule.voiceAttemptsUsed,
    abandonedAt: d.abandonedAt,
    recoveredAt: d.recoveredAt,
    funnel: d.funnel,
    offerId: d.offerId,
    situacao: situacaoDoLead(d),
    contacts: contatosQueContam(d.timeline).length,
    lastContact: ultimoContato(d.timeline),
    nextTouch: proximoToque(d),
    highValue: d.highValue,
  };
}

function poolDaEmpresa(companyId: string): PoolDaEmpresa {
  const emCache = CACHE_POOL.get(companyId);
  if (emCache !== undefined) {
    return emCache;
  }
  const bases: LeadBase[] = [...LEADS_ESPECIAIS];
  for (let i = LEADS_ESPECIAIS.length + 1; i <= TAMANHO_DO_POOL; i += 1) {
    const b = gerarBase(companyId, i);
    if (b !== null) {
      bases.push(b);
    }
  }
  // Limiar de alto valor: percentil 80 dos valores abandonados da empresa.
  // Percentil, e nao multiplo da media: numa empresa com quatro precos parecidos,
  // "2x a media" nao seleciona ninguem. Empates no limiar entram (catalogo
  // pequeno tem muitos carrinhos no mesmo preco).
  const limiar = limiarAltoValor(bases.map((b) => resolverOferta(companyId, b).amountCents));
  const detalhes = bases.map((b) => montarLead(companyId, b, limiar));
  const pool: PoolDaEmpresa = {
    detalhes,
    porId: new Map<string, LeadDetail>(detalhes.map((d) => [d.id, d] as const)),
    linhas: detalhes.map(linhaDoLead),
    limiarAltoValorCents: limiar,
  };
  CACHE_POOL.set(companyId, pool);
  return pool;
}

// -----------------------------------------------------------------------------
// Janela simulada
// -----------------------------------------------------------------------------

interface JanelaSimulada {
  readonly inicioMs: number;
  readonly fimMs: number;
  readonly dias: number;
  /** Janela imediatamente anterior, do mesmo tamanho; `null` em "Desde o inicio". */
  readonly anteriorInicioMs: number | null;
}

/** Interpreta a janela em TAMANHO, ancorada em AGORA_SIMULADO (ver cabecalho). "Tudo" cobre o pool inteiro. */
function janelaSimulada(range: PeriodRange): JanelaSimulada {
  const fimMs = AGORA_SIMULADO.getTime() + 60_000;
  if (range.from === null || range.to === null) {
    const dias = 400;
    return { inicioMs: fimMs - dias * DIA * 60_000, fimMs, dias, anteriorInicioMs: null };
  }
  const dias = Math.max(1, Math.min(400, Math.round((new Date(range.to).getTime() - new Date(range.from).getTime()) / 86_400_000)));
  const inicioMs = fimMs - dias * DIA * 60_000;
  return { inicioMs, fimMs, dias, anteriorInicioMs: inicioMs - dias * DIA * 60_000 };
}

function dentro(iso: string, inicioMs: number, fimMs: number): boolean {
  const t = new Date(iso).getTime();
  return t >= inicioMs && t < fimMs;
}

// -----------------------------------------------------------------------------
// API simulada
// -----------------------------------------------------------------------------

export function simularListaDeLeads(companyId: string, range: PeriodRange, consulta: ConsultaDeLeads): PaginaDeLeads {
  const pool = poolDaEmpresa(companyId);
  const janela = janelaSimulada(range);
  const noPeriodo = pool.linhas.filter((r) => dentro(r.abandonedAt, janela.inicioMs, janela.fimMs));
  const comBusca = noPeriodo.filter((r) => combinaComBusca(r, consulta.q));
  const facets = contarFacetas(comBusca, false);
  const filtradas = aplicarFiltros(comBusca, consulta);
  const ordenadas = ordenar(filtradas, consulta.sort, consulta.dir);
  const offset = Math.max(0, consulta.offset);
  return {
    items: ordenadas.slice(offset, offset + Math.max(1, consulta.limit)),
    total: ordenadas.length,
    limit: consulta.limit,
    offset,
    facets,
    highValueThresholdCents: pool.limiarAltoValorCents,
  };
}

export function simularResumoDeLeads(companyId: string, range: PeriodRange): ResumoDeLeads {
  const pool = poolDaEmpresa(companyId);
  const janela = janelaSimulada(range);
  // Detalhe e linha andam juntos (mesma ordem no pool): a situacao ja esta calculada na linha.
  const pares = pool.detalhes.map((d, i) => ({ d, linha: pool.linhas[i] ?? linhaDoLead(d) }));
  type Par = (typeof pares)[number];
  const atuais = pares.filter((p) => dentro(p.d.abandonedAt, janela.inicioMs, janela.fimMs));
  const anteriores = janela.anteriorInicioMs === null ? null : pares.filter((p) => dentro(p.d.abandonedAt, janela.anteriorInicioMs ?? 0, janela.inicioMs));

  const soma = (lista: readonly Par[]): number => lista.reduce((acc, p) => acc + p.d.amountCents, 0);
  const recuperados = (lista: readonly Par[]): readonly Par[] => lista.filter((p) => p.d.conversion !== null && !p.d.conversion.reversed);
  const esgotados = (lista: readonly Par[]): readonly Par[] => lista.filter((p) => p.linha.situacao === 'esgotado' || p.linha.situacao === 'falhou');

  const recAtual = recuperados(atuais);
  const esgAtual = esgotados(atuais);
  const naRegua = atuais.filter((p) => p.linha.situacao === 'na_fila' || p.linha.situacao === 'em_recuperacao');
  const comComercial = atuais.filter((p) => p.linha.situacao === 'com_comercial');
  const altoValorAberto = atuais.filter((p) => p.d.highValue && SITUACAO[p.linha.situacao].emAberto);
  const altoValorEsgotado = esgAtual.filter((p) => p.d.highValue);
  const agoraMs = AGORA_SIMULADO.getTime();
  const comComercialParado = comComercial.filter((p) => p.linha.lastContact !== null && agoraMs - new Date(p.linha.lastContact.at).getTime() > DIA * 60_000);
  const maisAntigo = comComercialParado.map((p) => p.linha.lastContact?.at ?? '').filter((a) => a !== '').sort()[0] ?? null;
  const falhas = atuais.filter((p) => p.linha.situacao === 'falhou');
  const emBreve = naRegua.filter((p) => p.linha.nextTouch !== null && new Date(p.linha.nextTouch.scheduledAt).getTime() - agoraMs <= 30 * 60_000);
  const porOferta = new Map<string, { readonly name: string; total: number; recuperados: number }>();
  for (const p of atuais) {
    if (p.d.offerId === null) {
      continue;
    }
    const atual = porOferta.get(p.d.offerId) ?? { name: p.d.productName ?? p.d.offerId, total: 0, recuperados: 0 };
    atual.total += 1;
    if (p.d.conversion !== null && !p.d.conversion.reversed) {
      atual.recuperados += 1;
    }
    porOferta.set(p.d.offerId, atual);
  }
  const semRecuperacao = [...porOferta.entries()].filter(([, v]) => v.total >= 6 && v.recuperados === 0).sort((a, b) => b[1].total - a[1].total)[0];

  return {
    companyId,
    periodStart: new Date(janela.inicioMs).toISOString(),
    periodEnd: new Date(janela.fimMs).toISOString(),
    abandoned: atuais.length,
    abandonedAmountCents: soma(atuais),
    recovered: recAtual.length,
    recoveredAmountCents: recAtual.reduce((acc, p) => acc + (p.d.conversion?.amountCents ?? 0), 0),
    commissionCents: recAtual.reduce((acc, p) => acc + (p.d.conversion?.commissionCents ?? 0), 0),
    inRule: naRegua.length,
    inRuleAmountCents: soma(naRegua),
    withSeller: comComercial.length,
    exhausted: esgAtual.length,
    exhaustedAmountCents: soma(esgAtual),
    highValueOpen: altoValorAberto.length,
    highValueOpenAmountCents: soma(altoValorAberto),
    highValueThresholdCents: pool.limiarAltoValorCents,
    highValueExhausted: altoValorEsgotado.length,
    highValueExhaustedAmountCents: soma(altoValorEsgotado),
    withSellerOver1d: comComercialParado.length,
    withSellerOver1dAmountCents: soma(comComercialParado),
    oldestSellerContactAt: maisAntigo,
    failed: falhas.length,
    dueSoon: emBreve.length,
    dueSoonAmountCents: soma(emBreve),
    offerWithoutRecovery: semRecuperacao === undefined ? null : { offerId: semRecuperacao[0], name: semRecuperacao[1].name, abandoned: semRecuperacao[1].total },
    previous:
      anteriores === null
        ? null
        : {
            abandoned: anteriores.length,
            abandonedAmountCents: soma(anteriores),
            recovered: recuperados(anteriores).length,
            recoveredAmountCents: recuperados(anteriores).reduce((acc, p) => acc + (p.d.conversion?.amountCents ?? 0), 0),
            exhausted: esgotados(anteriores).length,
          },
  };
}

export function simularDetalheDoLead(companyId: string, leadId: string): LeadDetail | null {
  return poolDaEmpresa(companyId).porId.get(leadId) ?? null;
}

/** Exposto para a previa calcular a comissao de uma venda marcada a mao, com a mesma regra do gerador. */
export function comissaoSimuladaCents(amountCents: number, feeBps: number): number {
  return comissaoCents(amountCents, feeBps);
}
